/* eslint-disable @typescript-eslint/no-explicit-any */
import { t } from "@lingui/macro"
import { makeAutoObservable } from "mobx"
import React from "react"

import { FieldTypeDropdownKeys } from "src/components/Inputs/types/IFieldTypeDropdown"

import {
    ConnectorAdminService,
    ContactFormV2AdminService,
    contactv2_DynamicFieldOption,
    contactv2_FormFieldSource,
    contactv2_FormMetadataField,
    contactv2_FormMetadataFieldSource,
    contactv2_FormType,
    contactv2_FormTypeField,
    contactv2_FormTypeWithSegments,
} from "src/api"
import { Channel } from "src/channel"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { FormFields } from "src/lib/form-fields"
import { reportError } from "src/lib/report"
import { OutputType } from "src/types/output"
import { IIntegrationConnectorItem } from "src/views/integration-connector/type"
import { FIELD_TYPE_DYNAMIC_FIELD } from "src/modals/contact-forms-form-builder/constants/fieldTypes"

export class FormDetailStore {
    static Context = React.createContext<FormDetailStore | null>(null)
    formFields = new FormFields<IFormFields>({
        name: "",
        type: "",
        description: "",
        accessGroupId: -1,
        segmentIds: [],
        email: [],
        externalFieldList: [],
        selectedConnectorId: -1,
        formFieldDescription: "",
        outputTypeSelectedValue: "" as OutputType,
        formFieldTitle: "",
        externalFormId: "",
        iconUrl: "",
        fields: [],
        messagingEnabled: false,
        showInUI: true,
        metaDataFields: [],
        metaDataFieldsList: [],
        aiInstructions: "",
        dimensionIds: [],
    })

    isLoading = false
    segmentIds: number[] = []
    accessGroupId: number = DEFAULT_ACCESS_GROUP.id
    hasError: boolean = false
    isEditMode = false
    dynamicFieldOptionsBySource: Map<string, contactv2_DynamicFieldOption[]> =
        new Map()
    loadingDynamicFieldSources: Set<string> = new Set()
    currentDynamicFieldConnectorId: number | undefined = undefined

    constructor() {
        makeAutoObservable(this)
    }

    get fields() {
        return this.formFields.get("fields")
    }

    get externalDynamicFieldsList() {
        const externalFieldList = this.formFields.get("externalFieldList")
        const list = this.fields?.map((item) => ({
            isRequired: item.required ?? false,
            isHidden: item.hidden ?? false,
            fallbackOption: item.fallback_option ?? "",
            forceUser: item.force_user ?? false,
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            isAddOptionsVisible: item.options?.length ? true : false,
            externalFieldTypeDropdownValue: {
                value: item.source ?? "",
                errorMessage: "",
            },
            fieldTypeDropdownValue: {
                value: item.type ?? "",
                errorMessage: "",
            },
            label: { value: item.label ?? "", uuid: "", errorMessage: "" },
            inputList: item.options?.map((v) => ({
                value: v.option ?? "",
                errorMessage: "",
            })),
            question: { value: item.label ?? "", uuid: "", errorMessage: "" },
            uuid: item.uuid ?? "",
            relationshipDropdownValue: {
                value: item.parent_field_uuid ?? "",
                errorMessage: "",
            },
            helpingText: { value: item.tooltip ?? "", errorMessage: "" },
            sourceTypes: externalFieldList?.find(
                (s) => s.source === item.source,
            )?.types,
            dynamicFieldMetadata: item.metadata ?? {},
        }))

        return list
    }

    get dynamicFieldsList() {
        const list = this.fields?.map((item) => ({
            isRequired: item.required ?? false,
            isHidden: item.hidden ?? false,
            fallbackOption: item.fallback_option ?? "",
            forceUser: item.force_user ?? false,
            dropdownValue: { value: item.type ?? "", errorMessage: "" },
            label: { value: item.label ?? "", uuid: "", errorMessage: "" },
            inputList: item.options?.map((v) => ({
                value: v.option ?? "",
                uuid: v.uuid ?? "",
                errorMessage: "",
            })),
            answer: {
                value:
                    item.type === FieldTypeDropdownKeys.Paragraph ||
                    item.type === FieldTypeDropdownKeys.ShortAnswer
                        ? item.placeholder_value ?? ""
                        : "",
                uuid: "",
                errorMessage: "",
            },
            uuid: item.uuid ?? "",
            helpingText: { value: item.tooltip ?? "", errorMessage: "" },
        }))

        return list
    }

    get metaDataFields() {
        return this.formFields.get("metaDataFields").map((item) => ({
            source: { value: item.source ?? "", errorMessage: "" },
            label: { value: item.label ?? "", errorMessage: "" },
        }))
    }

    getOutputTypeSelectedValue = (
        integration_connector_id?: number,
        sendEmail?: boolean,
    ) => {
        if (
            integration_connector_id !== undefined &&
            integration_connector_id > 0
        ) {
            return OutputType.Integration
        }
        return sendEmail === true ? OutputType.Email : OutputType.CaseEngine
    }

    init = async (
        connectorItems: IIntegrationConnectorItem[],
        accessGroupId: number,
        formId?: number,
        mode?: string,
    ) => {
        this.setIsLoading(true)

        let response: contactv2_FormTypeWithSegments | undefined
        let integrationConnectorTypeResponse:
            | contactv2_FormFieldSource[]
            | undefined

        let metaDataSourcesResponse:
            | contactv2_FormMetadataFieldSource[]
            | undefined

        if (formId !== undefined) {
            response =
                await ContactFormV2AdminService.getV2AdminContactFormType1({
                    formTypeId: formId as number,
                })

            integrationConnectorTypeResponse = await this.getAllSources(
                response.integration_connector_id,
            )
            metaDataSourcesResponse = await this.getMetaDataSources(
                response.integration_connector_id,
            )

            // Fetch dynamic fields for ALL unique sources with type="dynamic_field"
            const dynamicFields =
                response.fields?.filter((field) => {
                    const sourceInfo = integrationConnectorTypeResponse?.find(
                        (s) => s.source === field.source,
                    )
                    return sourceInfo?.types?.includes(FIELD_TYPE_DYNAMIC_FIELD)
                }) ?? []

            const dynamicFieldSources = dynamicFields
                .map((f) => f.source)
                .filter(
                    (source): source is NonNullable<typeof source> =>
                        source !== undefined,
                )

            // Get unique sources
            const uniqueSources = [...new Set(dynamicFieldSources)]

            const connectorId = response.integration_connector_id
            if (
                uniqueSources.length > 0 &&
                connectorId !== undefined &&
                connectorId > 0
            ) {
                await Promise.all(
                    uniqueSources.map((source) =>
                        this.getDynamicFieldsForSource(connectorId, source),
                    ),
                )
            }
        }

        this.formFields.init({
            connectorList: connectorItems,
            segmentIds: response?.published_in ?? [],
            dimensionIds: response?.dimension_ids ?? [],
            name: response?.name != null ? response.name : "",
            description: response?.description ?? "",
            email: response?.email ?? [],
            iconUrl: response?.icon_url,
            accessGroupId: response?.access_group_id ?? accessGroupId,
            fields: response?.fields ?? [],
            selectedConnectorId: response?.integration_connector_id,
            externalFormId: response?.external_id ?? "",
            sendEmail: response?.send_email,
            formFieldTitle:
                response?.fields != null ? response.fields[0].name : "",
            formFieldDescription:
                response?.fields != null ? response.fields[0].description : "",
            outputTypeSelectedValue:
                response !== undefined
                    ? this.getOutputTypeSelectedValue(
                          response.integration_connector_id,
                          response.send_email,
                      )
                    : undefined,
            externalFieldList: integrationConnectorTypeResponse ?? undefined,
            messagingEnabled: response?.messaging_enabled ?? false,
            showInUI: response?.show_in_ui ?? true,
            useAiTitle: response?.use_ai_title ?? false,
            metaDataFields: response?.metadata_fields ?? [],
            metaDataFieldsList: metaDataSourcesResponse ?? [],
            showTenantDetailsConfirmation:
                response?.contact_details_editable ?? false,
            aiInstructions: response?.llm_info_text ?? "",
        })

        if (mode === "Copy") {
            this.formFields.set("segmentIds", [])
            this.formFields.set("dimensionIds", [])
            this.formFields.set(
                "name",
                response?.name != null ? `${response.name} (Copy)` : "",
            )
            this.formFields.set("accessGroupId", accessGroupId)
        }

        this.isEditMode =
            mode === "Copy" ? false : response?.form_type_id !== undefined
        this.setIsLoading(false)
    }

    async getAllIntegrations(
        type: string,
    ): Promise<{ items: IIntegrationConnectorItem[] }> {
        const response =
            await ConnectorAdminService.getV1AdminConnectorIntegration({
                type,
            })
        const items: IIntegrationConnectorItem[] =
            response.map((item) => ({
                id: item.integration_id ?? -1,
                accessGroupId: item.access_group_id ?? -1,
                accessType: item.access_type ?? "",
                createdAt: item.created_at ?? "",
                deletedAt: item.deleted_at ?? "",
                integrationId: item.integration_id ?? -1,
                integrationTypeId: item.integration_type_id ?? -1,
                metaData: item.metadata as Record<string, string>,
                name: item.name ?? "",
                type: item.type ?? "",
                updatedAt: item.updated_at ?? "",
            })) ?? []

        return {
            items,
        }
    }

    async getAllSources(integrationConnectorId?: number) {
        if (
            integrationConnectorId !== undefined &&
            integrationConnectorId > 0
        ) {
            return await ContactFormV2AdminService.getV2AdminContactFormTypeConnectorSource(
                {
                    integrationConnectorId,
                },
            )
        }
    }

    async getMetaDataSources(integrationConnectorId?: number) {
        if (
            integrationConnectorId !== undefined &&
            integrationConnectorId > 0
        ) {
            return await ContactFormV2AdminService.getV2AdminContactFormTypeConnectorMetadataSource(
                {
                    integrationConnectorId,
                },
            )
        }
    }

    async getDynamicFieldsForSource(
        integrationConnectorId: number,
        source: string,
    ) {
        if (integrationConnectorId > 0 && source !== "") {
            // Clear cache and loading state if connector changed
            if (
                this.currentDynamicFieldConnectorId !== undefined &&
                this.currentDynamicFieldConnectorId !== integrationConnectorId
            ) {
                this.dynamicFieldOptionsBySource.clear()
                this.loadingDynamicFieldSources.clear()
            }
            this.currentDynamicFieldConnectorId = integrationConnectorId

            // Return early if data is already cached or request is in flight
            if (
                this.dynamicFieldOptionsBySource.has(source) ||
                this.loadingDynamicFieldSources.has(source)
            ) {
                return
            }

            this.loadingDynamicFieldSources.add(source)
            try {
                const options =
                    await ContactFormV2AdminService.getV2AdminContactFormTypeConnectorDynamicFields(
                        {
                            integrationConnectorId,
                            source,
                        },
                    )
                // Only write to cache if connector ID is still current
                if (
                    this.currentDynamicFieldConnectorId ===
                    integrationConnectorId
                ) {
                    this.dynamicFieldOptionsBySource.set(source, options)
                }
            } catch (error) {
                reportError(
                    t`contact-form-forms-view.fetch-dynamic-fields-fail`,
                    error,
                )
                // Only write empty array if connector ID is still current
                if (
                    this.currentDynamicFieldConnectorId ===
                    integrationConnectorId
                ) {
                    this.dynamicFieldOptionsBySource.set(source, [])
                }
            } finally {
                this.loadingDynamicFieldSources.delete(source)
            }
        }
    }

    isLoadingDynamicFieldSource = (source: string): boolean => {
        return this.loadingDynamicFieldSources.has(source)
    }

    getDynamicFieldOptionsForSource = (
        source: string,
    ): contactv2_DynamicFieldOption[] => {
        return this.dynamicFieldOptionsBySource.get(source) ?? []
    }

    createUpdateForm = async (id: number, request: contactv2_FormType) => {
        this.setIsLoading(true)
        const publishedIn = this.formFields.get("segmentIds")
        const dimensionIds = this.formFields.get("dimensionIds")
        let itemResponse: any = {}
        let formId = null

        try {
            if (!this.isEditMode) {
                // Create
                await this.formFields.catchErrors(async () => {
                    itemResponse =
                        await ContactFormV2AdminService.postV2AdminContactFormType(
                            {
                                request,
                            },
                        )
                })
                formId = itemResponse.form_type_id
            } else {
                // Update
                await this.formFields.catchErrors(async () => {
                    itemResponse =
                        await ContactFormV2AdminService.putV2AdminContactFormType(
                            {
                                formTypeId: id,
                                request,
                            },
                        )
                })
                formId = id
            }
            // Publish
            if (formId !== null) {
                await ContactFormV2AdminService.putV2AdminContactFormTypePublish(
                    {
                        formTypeId: formId as number,
                        request: {
                            published_in: publishedIn,
                            dimension_ids: dimensionIds,
                        },
                    },
                )
            }
            Channel.send({
                name: "repository/updated",
                payload: {
                    repository: "contact-forms-v2",
                    action: this.isEditMode ? "update" : "create",
                },
            })
        } catch (error) {
            reportError(t`contact-form-forms-view.update-form-fail`, error)
        } finally {
            this.setIsLoading(false)
        }
    }

    validateRequiredFields = () => {
        const fieldList1: string[] = [
            "name",
            "description",
            "accessGroupId",
            "iconUrl",
            "selectedConnectorId",
            "email",
        ]

        const fieldList2: string[] = [
            "name",
            "description",
            "accessGroupId",
            "iconUrl",
        ]

        const requiredFields =
            this.formFields.get("outputTypeSelectedValue") ===
            OutputType.Integration
                ? fieldList1
                : fieldList2

        this.formFields.clearErrors()

        requiredFields.forEach((field) => {
            const requiredField = field as keyof IFormFields
            const formField = this.formFields.get(requiredField)

            if (
                formField == null ||
                formField.toString().trim() === "" ||
                formField === -1 ||
                formField === "<p></p>\n"
            ) {
                this.formFields.setError(requiredField, t`errors.required`)
            } else {
                this.formFields.clearError(requiredField)
            }
        })
    }

    setIsLoading = (isLoading: boolean) => (this.isLoading = isLoading)
    setHasError = (hasError: boolean) => (this.hasError = hasError)
}

export interface IFormFields {
    name?: string
    type?: string
    description?: string
    accessGroupId?: number
    segmentIds?: number[]
    outputTypeSelectedValue?: OutputType
    email?: string[]
    connectorList?: IIntegrationConnectorItem[]
    externalFieldList?: {
        types?: string[]
        source?: string
        internal?: boolean
    }[]
    selectedConnectorId?: number
    formFieldDescription?: string
    formFieldTitle?: string
    iconUrl?: string
    fields?: Array<contactv2_FormTypeField>
    sendEmail?: boolean
    externalFormId?: string
    messagingEnabled?: boolean
    showInUI?: boolean
    useAiTitle?: boolean
    metaDataFields: Array<contactv2_FormMetadataField>
    metaDataFieldsList?: contactv2_FormMetadataFieldSource[]
    showTenantDetailsConfirmation?: boolean
    aiInstructions?: string
    dimensionIds?: number[]
}
